import { useEffect, useRef, useState } from "react";
import "./TechPoolSelector.css";
import { useHub } from "../../../../core/Global";
import Technician from "../../../technicians/models/technician";
import Avatar from "../../../../components/base/Avatar";
import FlexRow from "../../../../components/base/FlexRow";
import Icon from "../../../../components/base/Icon";
import { API_ROUTES } from "../../../../core/Constants";


interface IProps {
    selectedTechIds?: string[]
    // Ordered subset of selectedTechIds: the position IS the priority number
    // (index 0 => priority 1). Technicians missing from it have no priority.
    priorityTechIds?: string[]
    onChange: (techs: string[]) => void
    onPriorityChange?: (techIds: string[]) => void
}
export default function TechPoolSelector(props: IProps) {

    const hub = useHub()
    const poolSearchInput = useRef<HTMLInputElement>(null)
    const selSearchInput = useRef<HTMLInputElement>(null)

    const [firstLoad, firstLoadSet] = useState<boolean>(true)
    const [pool, poolSet] = useState<Technician[]>([])
    const [selectedTechs, selectedTechsSet] = useState<Technician[]>([])
    const [priorityIds, priorityIdsSet] = useState<string[]>([])
    const [draggingId, draggingIdSet] = useState<string>("")
    // Which row the insertion line is drawn on, and on which side of it.
    const [dropTarget, dropTargetSet] = useState<{ id: string, below: boolean }>()

    const [poolKeyword, poolKeywordSet] = useState<string>("")
    const [selKeyword, selKeywordSet] = useState<string>("")

    useEffect(() => {
        if (props.selectedTechIds && pool.length > 0 && firstLoad) {
            let p = pool;
            let s = selectedTechs;
            props.selectedTechIds.forEach(id => {
                const t = p.filter(f => f.id == id)[0]
                if (t) {
                    p.splice(pool.indexOf(t), 1);
                    s.push(t);
                }
            });
            poolSet(e => [...p]);
            selectedTechsSet(e => [...s]);
            priorityIdsSet((props.priorityTechIds ?? []).filter(id => s.some(f => f.id == id)));
            firstLoadSet(false)
        }
    }, [props.selectedTechIds, pool])

    useEffect(() => {
        var ids = selectedTechs.map(f => f.id)
        props.onChange(ids)
    }, [selectedTechs])

    useEffect(() => {
        props.onPriorityChange?.(priorityIds)
    }, [priorityIds])

    useEffect(() => {
        fetchPool()
    }, [])


    const fetchPool = () => {
        hub.Get(API_ROUTES.Technicians + "all", res => {
            if (res.success) {
                poolSet(res.data)
            }
        })
    }

    const addTech = (item: Technician) => {
        poolSet(prev => prev.filter(f => f.id != item.id));
        selectedTechsSet(prev => [...prev, item]);
    }

    const removeTech = (item: Technician) => {
        selectedTechsSet(prev => prev.filter(f => f.id != item.id));
        poolSet(prev => [...prev, item]);
        priorityIdsSet(prev => prev.filter(f => f != item.id));
    }

    // Add All takes the active technicians and leaves the suspended ones in the pool. Once only
    // suspended ones are left, the button says so and a second press adds them as well.
    const onlySuspendedLeft = pool.length > 0 && pool.every(f => f.suspended);
    const addAll = () => {
        const adding = onlySuspendedLeft ? pool : pool.filter(f => !f.suspended);
        selectedTechsSet(prev => [...prev, ...adding]);
        poolSet(prev => prev.filter(f => !adding.includes(f)));
    }

    const removeAll = () => {
        poolSet(prev => [...prev, ...selectedTechs]);
        selectedTechsSet([]);
        priorityIdsSet([]);
    }

    // Turning priority ON appends the technician to the ordered list so it takes the
    // next free number; turning it OFF renumbers the ones below it automatically.
    const togglePriority = (item: Technician) => {
        priorityIdsSet(prev => prev.includes(item.id)
            ? prev.filter(f => f != item.id)
            : [...prev, item.id]);
    }

    const endDrag = () => {
        draggingIdSet("");
        dropTargetSet(undefined);
    }

    const dropOnPriority = () => {
        const target = dropTarget;
        if (!draggingId || !target || draggingId == target.id) {
            endDrag();
            return;
        }
        priorityIdsSet(prev => {
            const from = prev.indexOf(draggingId);
            let to = prev.indexOf(target.id);
            if (from < 0 || to < 0) return prev;
            const next = [...prev];
            next.splice(from, 1);
            // Pulling the dragged id out shifts every later index down by one.
            if (from < to) to--;
            if (target.below) to++;
            next.splice(to, 0, draggingId);
            return next;
        });
        endDrag();
    }

    const sort = (a: Technician, b: Technician) => {
        if (a.suspended === b.suspended) {
            return a.fullName.localeCompare(b.fullName);
        }
        return (a.suspended ? 1 : 0) - (b.suspended ? 1 : 0);
    }

    const matches = (item: Technician, keyword: string) =>
        item.fullName.toLowerCase().includes(keyword.trim().toLowerCase())

    // Prioritized technicians are pinned to the top in priority order; the rest keep
    // the default suspended-last / alphabetical order.
    const orderedSelectedTechs = [
        ...priorityIds
            .map(id => selectedTechs.filter(f => f.id == id)[0])
            .filter(f => !!f),
        ...selectedTechs.filter(f => !priorityIds.includes(f.id)).sort(sort)
    ]

    // Reordering while the list is filtered would move a technician relative to rows
    // that aren't on screen, so dragging is only offered on the full list.
    const canSort = selKeyword.trim() == ""


    return (
        <div className="row">
            <div className="col-6">
                <FlexRow className="pb-2">
                    <div className="fs-5 fw-bolder">
                        Technicians Pool
                    </div>
                    {
                        pool.length > 0 &&
                        <span className={"badge cursor-pointer ms-auto " + (onlySuspendedLeft ? "badge-warning" : "badge-primary")}
                            title={onlySuspendedLeft ? "Only suspended technicians are left: add them all too" : "Add every active technician; suspended ones stay here"}
                            onClick={e => addAll()}>
                            <Icon icon="plus" size={12} className="me-1" />
                            {onlySuspendedLeft ? `Add ${pool.length} Suspended` : "Add All"}
                        </span>
                    }
                </FlexRow>
                <div>
                    <div>
                        {
                            pool.length > 0 &&
                            <div className="w-100 position-relative">
                                <input ref={poolSearchInput} type="text" className={`search-input form-control form-control-sm`}
                                    placeholder="Search..." value={poolKeyword} onChange={e => poolKeywordSet(e.target.value)}
                                />
                                {
                                    poolKeyword.length > 0 ?
                                        <span className="btn btn-flush btn-active-color-primary position-absolute top-50 end-0 translate-middle-y lh-0 me-4"
                                            onClick={e => {
                                                e.preventDefault()
                                                poolKeywordSet("")
                                                poolSearchInput.current?.focus()
                                            }}>

                                            <span className="svg-icon svg-icon-2 svg-icon-lg-1 me-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                                                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                                                </svg>
                                            </span>

                                        </span>
                                        :
                                        <span className="svg-icon svg-icon-2 search-icon position-absolute top-50 end-0 translate-middle-y me-5">
                                            <Icon icon="search" size={14} />
                                        </span>
                                }

                            </div>
                        }
                        <div className="scroll h-500px">
                            {
                                pool.length > 0 ?
                                    <div>
                                        <table className="table table-striped">
                                            <tbody>
                                                {
                                                    pool.filter(f => matches(f, poolKeyword))
                                                        .sort(sort).map((item, key) =>
                                                            <tr key={item.id}>
                                                                <td className="px-3 align-middle">
                                                                    <FlexRow>
                                                                        <Avatar avatarUrl={item.avatar} fullName={item.fullName}
                                                                            width={30} className="me-2" />
                                                                        <div>
                                                                            <div className="fs-7 limited-text"
                                                                                style={{
                                                                                    maxWidth: 300
                                                                                }}>
                                                                                {item.fullName}
                                                                            </div>
                                                                            {
                                                                                item.suspended ?
                                                                                    <div className="text-danger fs-9">
                                                                                        Suspended
                                                                                    </div>
                                                                                    :
                                                                                    <div className="text-muted fs-9">
                                                                                        {item.phoneItem?.phoneFriendly}
                                                                                    </div>
                                                                            }

                                                                        </div>
                                                                    </FlexRow>
                                                                </td>
                                                                <td className="text-end px-3 align-middle">
                                                                    <span className="badge badge-light-primary cursor-pointer"
                                                                        onClick={e => addTech(item)}>
                                                                        <Icon icon="plus" size={12} />
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        )
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                    :
                                    <div className="text-center pt-10">
                                        There is no technician!
                                    </div>
                            }

                        </div>
                    </div>

                </div>
            </div>
            <div className="col-6">

                <FlexRow className="border-bottom pb-2 ">
                    <div className="fs-5 fw-bolder">
                        Selected Technicians
                    </div>

                    {
                        selectedTechs.length > 0 &&
                        <span className="badge badge-danger cursor-pointer ms-auto"
                            onClick={e => removeAll()}>
                            <Icon icon="minus" size={12} className="me-1" />
                            Remove All
                        </span>
                    }

                </FlexRow>
                <div>


                    <div>
                        {
                            selectedTechs.length > 0 &&
                            <div className="w-100 position-relative">
                                <input ref={selSearchInput} type="text" className={`search-input form-control form-control-sm`}
                                    placeholder="Search..." value={selKeyword} onChange={e => selKeywordSet(e.target.value)}
                                />
                                {
                                    selKeyword.length > 0 ?
                                        <span className="btn btn-flush btn-active-color-primary position-absolute top-50 end-0 translate-middle-y lh-0 me-4"
                                            onClick={e => {
                                                e.preventDefault()
                                                selKeywordSet("")
                                                selSearchInput.current?.focus()
                                            }}>

                                            <span className="svg-icon svg-icon-2 svg-icon-lg-1 me-0">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                                    <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                                                    <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                                                </svg>
                                            </span>

                                        </span>
                                        :
                                        <span className="svg-icon svg-icon-2 search-icon position-absolute top-50 end-0 translate-middle-y me-5">
                                            <Icon icon="search" size={14} />
                                        </span>
                                }

                            </div>
                        }
                        <div className="scroll h-500px">
                            {
                                selectedTechs.length > 0 ?
                                    <table className="table table-striped">
                                        <tbody>
                                            {
                                                orderedSelectedTechs.filter(f => matches(f, selKeyword))
                                                    .map((item, key) => {

                                                        const priority = priorityIds.indexOf(item.id)
                                                        const prioritized = priority > -1
                                                        const canDrag = prioritized && canSort

                                                        const dropping = dropTarget?.id == item.id && draggingId != item.id

                                                        return (
                                                            <tr key={item.id}
                                                                className={"tech-pool-row"
                                                                    + (draggingId == item.id ? " tech-pool-dragging" : "")
                                                                    + (dropping ? (dropTarget?.below ? " tech-pool-drop-after" : " tech-pool-drop-before") : "")}
                                                                onDragOver={e => {
                                                                    if (!draggingId) return
                                                                    // Rows that can't take the drop clear the line instead of
                                                                    // leaving it stale on the last row that could.
                                                                    if (!canDrag) {
                                                                        dropTargetSet(undefined)
                                                                        return
                                                                    }
                                                                    e.preventDefault()
                                                                    const rect = e.currentTarget.getBoundingClientRect()
                                                                    const below = e.clientY > rect.top + (rect.height / 2)
                                                                    dropTargetSet(prev =>
                                                                        prev?.id == item.id && prev?.below == below
                                                                            ? prev
                                                                            : { id: item.id, below })
                                                                }}
                                                                onDrop={() => {
                                                                    if (canDrag) dropOnPriority()
                                                                }}>
                                                                <td className="ps-3 pe-0 align-middle" style={{ width: 1 }}>
                                                                    {
                                                                        canDrag ?
                                                                            // The drag ghost is the whole row, not just the handle.
                                                                            <span draggable
                                                                                title="Drag to reorder"
                                                                                style={{ cursor: "move" }}
                                                                                onDragStart={e => {
                                                                                    draggingIdSet(item.id)
                                                                                    const row = (e.target as HTMLElement).closest("tr")
                                                                                    if (row) e.dataTransfer.setDragImage(row, 24, row.offsetHeight / 2)
                                                                                }}
                                                                                onDragEnd={() => endDrag()}>
                                                                                <Icon icon="grip-lines" className="text-muted" size={12} minWidth={16} />
                                                                            </span>
                                                                            :
                                                                            <span style={{ display: "inline-block", minWidth: 16 }}></span>
                                                                    }
                                                                </td>
                                                                <td className="px-3 align-middle">
                                                                    <FlexRow>
                                                                        <Avatar avatarUrl={item.avatar} fullName={item.fullName}
                                                                            width={30} className="me-2" />
                                                                        <div>
                                                                            <div className="fs-7 limited-text"
                                                                                style={{
                                                                                    maxWidth: 300
                                                                                }}>
                                                                                {item.fullName}
                                                                            </div>
                                                                            {
                                                                                item.suspended ?
                                                                                    <div className="text-danger fs-9">
                                                                                        Suspended
                                                                                    </div>
                                                                                    :
                                                                                    <div className="text-muted fs-9">
                                                                                        {item.phoneItem?.phoneFriendly}
                                                                                    </div>
                                                                            }

                                                                        </div>
                                                                    </FlexRow>
                                                                </td>
                                                                <td className="px-3 align-middle">
                                                                    <FlexRow justify="end">
                                                                        <span className={`badge badge-circle badge-sm cursor-pointer me-2 ${prioritized ? "badge-info" : "tech-pool-priority-off"}`}
                                                                            title={prioritized ? "Remove priority" : "Set priority"}
                                                                            onClick={e => togglePriority(item)}>
                                                                            {prioritized && priority + 1}
                                                                        </span>
                                                                        <span className="badge badge-light-danger cursor-pointer"
                                                                            onClick={e => removeTech(item)}>
                                                                            <Icon icon="minus" size={12} />
                                                                        </span>
                                                                    </FlexRow>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                            }
                                        </tbody>
                                    </table>
                                    :
                                    <div className="text-center pt-10">
                                        There is no technician!
                                    </div>
                            }

                        </div>
                    </div>



                </div>
            </div>

        </div>
    )
}
